import * as THREE from "../../libs/three.js/build/three.module.js";
import {DigitizationTools} from "./ToolRegistry.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";

import {T, t} from "./i18n/index.js";
// Værtens lag - 3D-baggrundskort, IFC/OBJ-modeller og GIS-lag - som objekter i Registreringer-træet (gruppen "Data & Lag"), så
// de kan tændes og slukkes dér. Samme mønster som nativeClip.js/nativeGraveReport.js: en feature uden egen visning (kun en række
// med øje og slet), men her ejes laget af værten (frontendens Vue-paneler), så værten leverer tre funktioner:
//
//   const featureId = dm.registerLayerObject({
//     key: "gis:<id>",        // unik nøgle (samme nøgle igen = erstatter)
//     type: "basemap" | "model" | "gis",
//     name: "...",
//     group: "LER",           // valgfri: undergruppe i Data & Lag (fx "LER", "WFS"); uden group ligger rækken direkte under Data & Lag
//     getVisible(), setVisible(boolean),   // træets øje; getVisible læses ved hver synkronisering
//     remove(),                            // træets papirkurv (værten fjerner selv laget)
//   });
//   dm.notifyLayerObjectChanged(key)      // værten ændrede synligheden andetsteds -> træet opdateres
//   dm.unregisterLayerObject(key)         // værten fjernede selv laget -> rækken forsvinder (remove() kaldes IKKE)
//
// Rækkerne har kind "layer" (egen gruppe, aldrig med i Gem registreringer) og ingen geometri af betydning: koordinaten er blot
// midten af den første indlæste punktsky (så intet, der gennemløber alle objekter, trækkes langt væk).

export const LAYER_TOOLS = {
	basemap: {id: "digitize_layer_basemap", name: T("3D-baggrundskort"), featureType: T("Baggrundskort")},
	model: {id: "digitize_layer_model", name: T("Model"), featureType: T("Model")},
	gis: {id: "digitize_layer_gis", name: T("GIS-lag"), featureType: "GIS"},
};

for(const tool of Object.values(LAYER_TOOLS)){
	DigitizationTools.register({
		id: tool.id,
		name: tool.name,
		category: "hidden",
		kind: "layer",
		icon: null,
		resultGeometryType: "Point",
		featureType: tool.featureType,
		tooltip: T("Lag fra værktøjerne Lag, IFC / Model og GIS - tænd og sluk her."),
		customCapture(){
			// intet: lag oprettes af værtens egne paneler
		},
	});
}

class LayerObjectVisual extends FeatureVisual {
	constructor(center){
		super("Point");
		this.editable = false;
		this._center = center;
		// et lag har ingen labels: ingen label-knap i træet
		this.setLabelsVisible = undefined;
	}

	rawCoordinates(){
		return [[this._center.x, this._center.y, this._center.z]];
	}

	toCoordinates(){
		return this.rawCoordinates()[0];
	}
}

function registry(dm){
	if(!dm._layerObjects) dm._layerObjects = new Map();
	return dm._layerObjects;
}

function anchorPoint(dm){
	const first = dm.viewer.scene.pointclouds[0];
	if(first && typeof first.getBoundingBoxWorld === "function"){
		const box = first.getBoundingBoxWorld();
		if(box && !box.isEmpty()) return box.getCenter(new THREE.Vector3());
	}
	return new THREE.Vector3();
}

export function registerLayerObject(dm, spec){
	if(!spec || !spec.key || !LAYER_TOOLS[spec.type]) return null;

	const objects = registry(dm);
	if(objects.has(spec.key)) unregisterLayerObject(dm, spec.key);

	const tool = LAYER_TOOLS[spec.type];
	const visual = new LayerObjectVisual(anchorPoint(dm));
	dm.scene.add(visual);

	const feature = createFeature({
		geometryType: "Point",
		coordinates: visual.toCoordinates(),
		category: "hidden",
		tool: tool.id,
		featureType: t(tool.featureType),
		name: spec.name || t(tool.name),
		attributes: {layerKey: spec.key, layerGroup: spec.group || null},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);

	// Træets øje styrer værtens lag.
	Object.defineProperty(visual, "visible", {
		configurable: true,
		get: () => spec.getVisible() !== false,
		set: (value) => spec.setVisible(!!value),
	});

	dm._wireFeatureLifecycle(feature, visual);
	dm.removeFeatureSnapTargets(feature.id);

	const entry = {featureId: feature.id, spec, released: false};
	objects.set(spec.key, entry);

	// Træets papirkurv (eller en anden fjernelse af rækken): værten fjerner selv laget - undtagen når værten selv har fjernet det.
	const onFeatureRemoved = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		dm.featureManager.removeEventListener("feature_removed", onFeatureRemoved);
		if(objects.get(spec.key) === entry) objects.delete(spec.key);
		if(entry.released) return;
		entry.released = true;
		try{
			if(spec.remove) spec.remove();
		}catch(error){
			console.error("Lag-objekt: fjernelse fejlede", error);
		}
	};
	dm.featureManager.addEventListener("feature_removed", onFeatureRemoved);

	dm.dispatchEvent({type: "feature_created", feature, visual});
	return feature.id;
}

// Værten har selv fjernet laget: rækken forsvinder, uden at spec.remove() kaldes.
export function unregisterLayerObject(dm, key){
	const entry = registry(dm).get(key);
	if(!entry) return false;
	entry.released = true;
	if(dm.getVisual(entry.featureId)) dm.removeFeature(entry.featureId);
	return true;
}

// Værten har ændret lagets synlighed andetsteds (fx et afkrydsningsfelt): læs den igen i træet.
export function notifyLayerObjectChanged(dm, key){
	if(!registry(dm).has(key)) return false;
	dm.dispatchEvent({type: "label_visibility_changed"});
	return true;
}

export function hasLayerObject(dm, key){
	return registry(dm).has(key);
}
